<template>
    <transition name="nc-viewer-fade">
        <div
            v-if="attachment"
            class="nc-viewer"
            role="dialog"
            aria-modal="true"
            @click.self="close"
        >
            <div class="nc-viewer__window" :class="`nc-viewer__window_${fileKind}`">

                <div class="nc-viewer__header">
                    <span class="nc-viewer__name" :title="attachment.name">{{ attachment.name }}</span>
                    <div class="nc-viewer__header-actions">
                        <a
                            v-if="attachment.url"
                            :href="attachment.url"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="nc-viewer__action"
                            title="Открыть в новой вкладке"
                            @click.stop
                        >
                            <svg width="16" height="16" aria-hidden="true">
                                <use href="#icon-attach"></use>
                            </svg>
                        </a>
                        <button
                            type="button"
                            class="nc-viewer__action nc-viewer__action_close"
                            title="Закрыть"
                            @click="close"
                        >
                            <svg width="14" height="14" aria-hidden="true">
                                <use href="#icon-close"></use>
                            </svg>
                        </button>
                    </div>
                </div>

                <div class="nc-viewer__body">

                    <!-- IMAGE -->
                    <template v-if="fileKind === 'image'">
                        <div class="nc-viewer__image-wrap">
                            <img
                                v-if="!loadError"
                                :src="attachment.url"
                                :alt="attachment.name"
                                class="nc-viewer__image"
                                @error="loadError = true"
                            />
                            <div v-else class="nc-viewer__error">
                                <svg width="40" height="40" aria-hidden="true">
                                    <use href="#icon-attach"/>
                                </svg>
                                <p>Не удалось загрузить изображение</p>
                                <a
                                    :href="attachment.url"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    class="nc-viewer__link"
                                >Открыть в новой вкладке</a>
                            </div>
                        </div>
                    </template>

                    <!-- VIDEO -->
                    <template v-else-if="fileKind === 'video'">
                        <div class="nc-viewer__video-wrap">
                            <video
                                v-if="!loadError"
                                controls
                                class="nc-viewer__video"
                                @error="loadError = true"
                            >
                                <source :src="attachment.url" :type="attachment.mimeType || undefined" />
                            </video>
                            <div v-if="loadError" class="nc-viewer__error">
                                <svg width="40" height="40" aria-hidden="true">
                                    <use href="#icon-attach"/>
                                </svg>
                                <p>Не удалось загрузить видео</p>
                                <a
                                    :href="attachment.url"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    class="nc-viewer__link"
                                >Открыть в новой вкладке</a>
                            </div>
                        </div>
                    </template>

                    <!-- AUDIO -->
                    <template v-else-if="fileKind === 'audio'">
                        <div class="nc-viewer__audio-wrap">
                            <svg width="56" height="56" class="nc-viewer__audio-icon" aria-hidden="true">
                                <use href="#icon-attach"/>
                            </svg>
                            <p class="nc-viewer__audio-name">{{ attachment.name }}</p>
                            <audio
                                v-if="!loadError"
                                controls
                                class="nc-viewer__audio"
                                @error="loadError = true"
                            >
                                <source :src="attachment.url" :type="attachment.mimeType || undefined" />
                            </audio>
                            <div v-if="loadError" class="nc-viewer__error">
                                <p>Не удалось загрузить аудио</p>
                                <a
                                    :href="attachment.url"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    class="nc-viewer__link"
                                >Открыть в новой вкладке</a>
                            </div>
                        </div>
                    </template>

                    <!-- PDF -->
                    <template v-else-if="fileKind === 'pdf'">
                        <iframe
                            v-if="!loadError"
                            :src="attachment.url"
                            class="nc-viewer__pdf"
                            title="PDF document"
                            @error="loadError = true"
                        />
                        <div v-if="loadError" class="nc-viewer__error">
                            <svg width="40" height="40" aria-hidden="true">
                                <use href="#icon-attach"/>
                            </svg>
                            <p>Не удалось загрузить PDF</p>
                            <a
                                :href="attachment.url"
                                target="_blank"
                                rel="noopener noreferrer"
                                class="nc-viewer__link"
                            >Открыть в новой вкладке</a>
                        </div>
                    </template>

                    <!-- UNSUPPORTED -->
                    <template v-else>
                        <div class="nc-viewer__unsupported">
                            <svg width="56" height="56" class="nc-viewer__unsupported-icon" aria-hidden="true">
                                <use href="#icon-attach"/>
                            </svg>
                            <p class="nc-viewer__unsupported-name">{{ attachment.name }}</p>
                            <p class="nc-viewer__unsupported-msg">
                                Предпросмотр данного типа файла не поддерживается
                            </p>
                            <a
                                v-if="attachment.url"
                                :href="attachment.url"
                                target="_blank"
                                rel="noopener noreferrer"
                                class="nc-viewer__link"
                            >
                                Скачать файл
                            </a>
                        </div>
                    </template>

                </div>
            </div>
        </div>
    </transition>
</template>

<script>
import { detectFileKind } from '../utils/attachment'

export default {
    name: 'NewChatAttachmentViewer',
    props: {
        attachment: {
            type: Object,
            default: null, // { url, name, mimeType }
        },
    },
    data() {
        return {
            loadError: false,
        }
    },
    computed: {
        fileKind() {
            if (!this.attachment) return 'unsupported'
            return detectFileKind(this.attachment.mimeType, this.attachment.name, this.attachment.apiType)
        },
    },
    watch: {
        attachment(val) {
            this.loadError = false
            if (val) {
                document.addEventListener('keydown', this.onKeyDown)
            } else {
                document.removeEventListener('keydown', this.onKeyDown)
            }
        },
    },
    beforeDestroy() {
        document.removeEventListener('keydown', this.onKeyDown)
    },
    methods: {
        close() {
            this.$emit('close')
        },
        onKeyDown(e) {
            if (e.key === 'Escape') this.close()
        },
    },
}
</script>

<style lang="scss">
@import '../../../../../gulp-src/app/scss/_variables';

.nc-viewer {
    position: fixed;
    inset: 0;
    z-index: 1010;
    background: rgba(0, 0, 0, 0.78);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 40px 16px;

    // --- Window ---
    &__window {
        position: relative;
        background: $global-white;
        border-radius: 12px;
        display: flex;
        flex-direction: column;
        overflow: hidden;
        box-shadow: 0 12px 48px rgba(0, 0, 0, 0.45);
        max-height: calc(100vh - 140px);
        width: 90vw;
        max-width: 960px;

        // Audio/Unsupported — компактне вікно
        &_audio,
        &_unsupported {
            width: 480px;
            max-width: 90vw;
        }

        // PDF — максимальна площа для читання
        &_pdf {
            width: 90vw;
            max-width: 1100px;
            height: calc(100vh - 140px);
        }
    }

    // --- Header ---
    &__header {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 10px 12px;
        border-bottom: 1px solid $global-light-gray;
        flex: 0 0 auto;
    }

    &__name {
        flex: 1 1 auto;
        font-size: 14px;
        font-weight: 500;
        color: $global-black;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    &__header-actions {
        display: flex;
        align-items: center;
        gap: 4px;
        flex: 0 0 auto;
    }

    &__action {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;
        border: none;
        background: none;
        border-radius: 8px;
        cursor: pointer;
        color: $global-gray;
        text-decoration: none;
        transition: background 0.15s ease, color 0.15s ease;

        svg {
            display: block;
        }

        &:hover {
            background: $global-white-f6;
            color: $global-black;
        }

        &_close:hover {
            background: #fee2e2;
            color: #ef4444;
        }
    }

    // --- Body ---
    &__body {
        flex: 1 1 auto;
        overflow: auto;
        display: flex;
        flex-direction: column;
        align-items: stretch;
        min-height: 0;
    }

    // --- Image ---
    &__image-wrap {
        flex: 1 1 auto;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 20px;
        overflow: auto;
    }

    &__image {
        display: block;
        max-width: 100%;
        max-height: calc(100vh - 240px);
        object-fit: contain;
        border-radius: 4px;
    }

    // --- Video ---
    &__video-wrap {
        flex: 1 1 auto;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 16px;
        background: #000;
    }

    &__video {
        max-width: 100%;
        max-height: calc(100vh - 130px);
        display: block;
    }

    // --- Audio ---
    &__audio-wrap {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 16px;
        padding: 36px 24px 32px;
    }

    &__audio-icon {
        color: $global-purple;
        opacity: 0.4;
    }

    &__audio-name {
        font-size: 14px;
        font-weight: 500;
        color: $global-black;
        text-align: center;
        margin: 0;
        word-break: break-word;
    }

    &__audio {
        width: 100%;
        max-width: 360px;
    }

    // --- PDF ---
    &__pdf {
        flex: 1 1 auto;
        width: 100%;
        border: none;
        display: block;
        min-height: 0;
    }

    // --- Unsupported ---
    &__unsupported {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 12px;
        padding: 44px 24px;
    }

    &__unsupported-icon {
        color: $global-gray;
        opacity: 0.35;
    }

    &__unsupported-name {
        font-size: 15px;
        font-weight: 600;
        color: $global-black;
        text-align: center;
        margin: 0;
        word-break: break-word;
    }

    &__unsupported-msg {
        font-size: 13px;
        color: $global-gray;
        text-align: center;
        margin: 0;
    }

    // --- Error ---
    &__error {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 12px;
        padding: 44px 24px;

        svg {
            color: $global-gray;
            opacity: 0.35;
        }

        p {
            font-size: 14px;
            color: $global-gray;
            text-align: center;
            margin: 0;
        }
    }

    // --- Link ---
    &__link {
        font-size: 13px;
        color: $global-purple;
        text-decoration: underline;
        cursor: pointer;

        &:hover {
            opacity: 0.75;
        }
    }
}

// Transition
.nc-viewer-fade-enter-active,
.nc-viewer-fade-leave-active {
    transition: opacity 0.2s ease;
}

.nc-viewer-fade-enter,
.nc-viewer-fade-leave-to {
    opacity: 0;
}
</style>
